// Tournament stats for an external tournament: players and teams, for the whole tournament,
// one match, or one map of a match (GET /api/v1/ext/tournaments/:tid/stats).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, input, sectionLabel, pill, RGB, Icon, ICONS, errMsg, ratingColor } from './extUi';
import { COLS, FLUX3_COLS, cellValue, ColHeader } from './ExtPlayerStats';

const th = { ...sectionLabel, padding: '0.45rem 0.45rem', textAlign: 'right', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
const td = { padding: '0.42rem 0.45rem', borderTop: '1px solid var(--border-subtle)', textAlign: 'right', fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: '0.78rem' };
const mapLabel = (n) => String(n || '?').replace(/^de_/, '');
const signed = (v) => (v > 0 ? `+${v}` : `${v}`);
const diffColor = (v) => (v > 0 ? 'var(--accent-green)' : v < 0 ? 'var(--accent-red)' : 'var(--text-secondary)');

// sortable value of a player column
const colValue = (c, p) => {
  if (c.key === 'entry') return p.entry_kills - p.opening_deaths;
  if (c.key === 'mk') return p.multikills['3k'] + p.multikills['4k'] * 2 + p.multikills['5k'] * 3;
  if (c.key === 'bomb') return p.bomb_plants + p.bomb_defuses;
  const v = cellValue(c, p);
  return typeof v === 'number' ? v : Number(v) || 0;
};

export function PlayersTable({ players }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: 'rating', desc: true });
  const cols = useMemo(() => [{ key: 'matches_played', label: 'M', title: 'Matches played' }, { key: 'rounds', label: 'Rds', title: 'Rounds played' }, ...COLS, ...FLUX3_COLS], []);
  const rows = useMemo(() => {
    const c = cols.find(x => x.key === sort.key);
    return [...players].sort((a, b) => {
      if (sort.key === 'name') return sort.desc ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      const d = colValue(c, a) - colValue(c, b);
      return sort.desc ? -d : d;
    });
  }, [players, sort, cols]);
  const click = (key) => setSort(s => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }));
  const arrow = (key) => (sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '');
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', width: 28 }}>#</th>
            <th style={{ ...th, textAlign: 'left' }} onClick={() => click('name')}>{t('ext.sb.player', 'Player')}{arrow('name')}</th>
            {cols.map(c => <ColHeader key={c.key} c={c} style={th} suffix={arrow(c.key)} onClick={() => click(c.key)} />)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length + 2} style={{ ...td, textAlign: 'left', fontFamily: 'inherit' }}>{t('extStats.noData', 'No stats yet for this selection.')}</td></tr>}
          {rows.map((p, i) => (
            <tr key={p.steam_id}>
              <td style={{ ...td, textAlign: 'left', color: 'var(--text-muted)' }}>{i + 1}</td>
              <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', color: 'var(--text-primary)', fontWeight: 600 }}>
                {p.name}
                {p.team && <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginLeft: '0.45rem', fontSize: '0.72rem' }}>{p.team.tag || p.team.name}</span>}
              </td>
              {cols.map(c => {
                const v = cellValue(c, p);
                const color = c.color ? c.color(v) : undefined;
                return <td key={c.key} style={{ ...td, color: color || td.color, fontWeight: c.key === 'rating' || c.key === 'kills' ? 700 : 400 }}>{c.fmt ? c.fmt(v) : v}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, hint, color }) {
  return (
    <div style={{ padding: '0.5rem 0.7rem', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', minWidth: 104 }}>
      <div style={{ ...sectionLabel, fontSize: '0.62rem' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '1.02rem', fontWeight: 700, color: color || 'var(--text-primary)', marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

const pctText = (x) => (x.win_pct == null ? '—' : `${x.win_pct}%`);

export function TeamCard({ team, wholeMatch, rank }) { // also the team detail of the panel's season leaderboard (leaderboard/SeasonBoard.js)
  const { t } = useTranslation();
  const tot = team.totals;
  const teamRating = Number((tot.avg_hltv_rating ?? tot.avg_rating) ?? 0);
  const reasons = [
    ['elimination', t('extStats.reason.elimination', 'Elimination')],
    ['bomb_exploded', t('extStats.reason.bomb_exploded', 'Bomb exploded')],
    ['bomb_defused', t('extStats.reason.bomb_defused', 'Bomb defused')],
    ['time', t('extStats.reason.time', 'Time ran out')],
  ];
  const maxReason = Math.max(1, ...reasons.map(([k]) => team.win_reasons[k] || 0));
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-muted)', fontSize: '0.8rem' }}>#{rank}</span>
        <h2 style={{ ...h2, margin: 0 }}>{team.name}</h2>
        {team.tag && <span style={pill(RGB.gray)}>{team.tag}</span>}
        {!team.team_id && <span style={pill(RGB.amber)} title={t('extStats.byNameHint', 'Not a tournament team: grouped by the team name typed on the match')}>{t('extStats.byName', 'by name')}</span>}
      </div>

      {/* results */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {wholeMatch && <Stat label={t('extStats.matches', 'Matches')} value={`${team.matches.won}-${team.matches.lost}${team.matches.draw ? `-${team.matches.draw}` : ''}`} hint={t('extStats.playedN', '{{n}} played', { n: team.matches.played })} />}
        <Stat label={t('extStats.maps', 'Maps')} value={`${team.maps.won}-${team.maps.lost}`} hint={t('extStats.playedN', '{{n}} played', { n: team.maps.played })} />
        <Stat label={t('extStats.rounds', 'Rounds')} value={`${team.rounds.won}-${team.rounds.lost}`} hint={t('extStats.playedN', '{{n}} played', { n: team.rounds.played })} />
        <Stat label={t('extStats.roundDiff', 'Round diff')} value={signed(team.rounds.diff)} color={diffColor(team.rounds.diff)} hint={pctText(team.rounds)} />
        <Stat label={t('extStats.ctWin', 'CT rounds')} value={pctText(team.ct)} hint={`${team.ct.won}/${team.ct.played}`} />
        <Stat label={t('extStats.tWin', 'T rounds')} value={pctText(team.t)} hint={`${team.t.won}/${team.t.played}`} />
        <Stat label={t('extStats.pistol', 'Pistol rounds')} value={pctText(team.pistol)} hint={`${team.pistol.won}/${team.pistol.played}`} />
      </div>

      {/* team totals */}
      <div style={{ ...sectionLabel, marginBottom: '0.35rem' }}>{t('extStats.totals', 'Team totals')}</div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        <Stat label="K / D" value={`${tot.kills} / ${tot.deaths}`} hint={`K/D ${tot.kd}`} />
        <Stat label="ADR" value={tot.adr} />
        <div title={t('ext.stat.ratingHltv', 'HLTV Rating 2.0 (estimate, same formula on every match)')}>
          <Stat label={`${t('extStats.avgRating', 'Avg rating')} · HLTV 2.0`} value={teamRating.toFixed(2)} color={ratingColor(teamRating)} />
        </div>
        <Stat label={t('extStats.entries', 'Entries')} value={`${tot.entry_kills}-${tot.opening_deaths}`} hint={t('extStats.entriesHint', 'first kills – first deaths')} />
        <Stat label={t('extStats.clutches', 'Clutches')} value={tot.clutches_won} />
        <Stat label={t('extStats.aces', 'Aces')} value={tot.aces} />
        <Stat label="HS%" value={`${tot.headshot_pct}%`} />
      </div>

      <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
        {/* win reasons */}
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ ...sectionLabel, marginBottom: '0.4rem' }}>{t('extStats.winReasons', 'Rounds won by')}</div>
          {reasons.map(([k, label]) => (
            <div key={k} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 36px', gap: '0.5rem', alignItems: 'center', fontSize: '0.76rem', marginBottom: 4 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
              <span style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${((team.win_reasons[k] || 0) / maxReason) * 100}%`, background: 'rgba(59,130,246,0.6)' }} />
              </span>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', textAlign: 'right', color: 'var(--text-primary)' }}>{team.win_reasons[k] || 0}</span>
            </div>
          ))}
        </div>

        {/* per-map record */}
        <div style={{ flex: '2 1 340px', minWidth: 0 }}>
          <div style={{ ...sectionLabel, marginBottom: '0.3rem' }}>{t('extStats.mapRecord', 'Per-map record')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', cursor: 'default' }}>{t('extStats.map', 'Map')}</th>
                <th style={{ ...th, cursor: 'default' }}>{t('extStats.played', 'Played')}</th>
                <th style={{ ...th, cursor: 'default' }}>W-L</th>
                <th style={{ ...th, cursor: 'default' }}>{t('extStats.rounds', 'Rounds')}</th>
                <th style={{ ...th, cursor: 'default' }}>+/-</th>
              </tr>
            </thead>
            <tbody>
              {team.map_record.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'left', fontFamily: 'inherit' }}>—</td></tr>}
              {team.map_record.map(r => (
                <tr key={r.map}>
                  <td style={{ ...td, textAlign: 'left', fontFamily: 'inherit', color: 'var(--text-primary)', textTransform: 'capitalize' }}>{mapLabel(r.map)}</td>
                  <td style={td}>{r.played}</td>
                  <td style={td}>{r.won}-{r.lost}</td>
                  <td style={td}>{r.rounds_won}-{r.rounds_lost}</td>
                  <td style={{ ...td, color: diffColor(r.diff), fontWeight: 700 }}>{signed(r.diff)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ExtTournamentStats() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [tn, setTn] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchSel, setMatchSel] = useState('');   // '' = whole tournament
  const [mapSel, setMapSel] = useState('all');
  const [tab, setTab] = useState('players');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/api/v1/ext/tournaments/${id}`).then(r => setTn(r.data)).catch(() => { });
    axios.get(`/api/v1/ext/tournaments/${id}/matches`).then(r => setMatches((r.data.data || []).filter(m => m.status !== 'cancelled'))).catch(() => { });
  }, [id]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (matchSel) { params.match_id = matchSel; if (mapSel !== 'all') params.map_index = mapSel; }
      const r = await axios.get(`/api/v1/ext/tournaments/${id}/stats`, { params });
      setData(r.data); setError('');
    } catch (e) { setError(errMsg(e, t('ext.loadFailed', 'Failed to load'))); }
  }, [id, matchSel, mapSel, t]);
  useEffect(() => { load(); const iv = setInterval(() => { if (!document.hidden) load(); }, 15000); return () => clearInterval(iv); }, [load]);

  const match = matches.find(m => String(m.id) === String(matchSel));
  // stored maps from the stats response (always there); the match list only has them while its parser is loaded
  const maps = !match ? []
    : (data && data.scope.maps && String(data.scope.match_id) === String(matchSel)) ? data.scope.maps
      : (match.maps || []).filter(m => m.status === 'live' || m.status === 'finished');
  const tabBtn = (k, label) => (
    <button type="button" onClick={() => setTab(k)} style={{ ...pill(tab === k ? RGB.blue : RGB.gray), cursor: 'pointer', fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}>{label}</button>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to={`/admin/tournaments/${id}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {tn ? tn.name : t('ext.backToTournament', 'Tournament')}</Link>
        <h1 style={{ ...h1, marginTop: '0.35rem' }}>{t('extStats.title', 'Stats')}</h1>
        <p style={{ ...sub, margin: '0.3rem 0 0' }}>{t('extStats.subtitle', 'Player and team statistics for the whole tournament, one match, or one map. Cancelled matches are not counted.')}</p>
      </div>

      {/* scope */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <span style={sectionLabel}>{t('extStats.show', 'Show')}</span>
        <select style={{ ...input, width: 'auto', minWidth: 260 }} value={matchSel} onChange={e => { setMatchSel(e.target.value); setMapSel('all'); }}>
          <option value="">{t('extStats.wholeTournament', 'Whole tournament')}</option>
          {matches.map(m => <option key={m.id} value={m.id}>{`${m.teams.team1.name} vs ${m.teams.team2.name} · BO${m.best_of}`}</option>)}
        </select>
        {match && (
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setMapSel('all')} style={{ ...pill(mapSel === 'all' ? RGB.blue : RGB.gray), cursor: 'pointer' }}>{t('ext.allMaps', 'All maps')}</button>
            {maps.map(m => (
              <button key={m.index} type="button" onClick={() => setMapSel(m.index)} style={{ ...pill(mapSel === m.index ? RGB.blue : RGB.gray), cursor: 'pointer', textTransform: 'capitalize' }}>
                {mapLabel(m.name || t('ext.mapN', 'Map {{n}}', { n: m.index + 1 }))}{m.score ? ` ${m.score.team1}-${m.score.team2}` : ''}
              </button>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {tabBtn('players', t('extStats.players', 'Players'))}
        {tabBtn('teams', t('extStats.teams', 'Teams'))}
      </div>

      {error && <div style={{ ...sub, color: 'var(--accent-red)' }}>{error}</div>}
      {!data && !error && <div style={sub}>{t('common.loading', 'Loading...')}</div>}

      {data && tab === 'players' && (
        <div style={card}>
          <PlayersTable players={data.players} />
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
            {t('extStats.playersLegend', 'M matches · Rds rounds · click a column to sort')} · {t('ext.statsLegend', 'FK-FD first kills – first deaths · TK trade kills · 1vX clutches won · FA flash assists · EF enemies flashed · UD utility damage · P/D bomb plants / defuses')}
          </div>
        </div>
      )}

      {data && tab === 'teams' && (
        <>
          {data.teams.length === 0 && <div style={card}><div style={sub}>{t('extStats.noData', 'No stats yet for this selection.')}</div></div>}
          {data.teams.map((tm, i) => <TeamCard key={tm.key} team={tm} rank={i + 1} wholeMatch={mapSel === 'all'} />)}
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t('extStats.teamsLegend', 'Pistol rounds are the first round of each half. Team totals add up what the team’s lineup did; avg rating is the average of the players’ ratings, weighted by rounds played.')}</div>
        </>
      )}
    </div>
  );
}
